import {
  getColorScheme,
  getLineChartHeight,
  getLineChartOptions,
  POINT_HOVER_RADIUS,
} from '../utils';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@customer/store/hooks';
import { useTranslation } from 'react-i18next';
import {
  ScenarioQueryParams,
  ScenarioTimeFrame,
  TimeUnit,
  useGetScenarioTimeSeriesQuery,
} from '@customer/store/apis/scenariosApi';
import { Line } from 'react-chartjs-2';
import { LineChartByProps } from '../config';
import { ChartOptions, ChartData, ChartDataset } from 'chart.js';
import useFilters from '@customer/filters/useFilters';
import { useLocationFilterParams } from '@customer/store/slices/locationFilterSlice';
import { customRangePlugin } from '../custom-range/CustomRangePlugin';
import { CustomTimeRangeParams } from '@customer/store/apis/helpers';
import { useChartScenarioTimeFilters } from '../hooks';
import ChartManager from '../chart-manager/ChartManager';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';
import { OnGetValue } from '../bar-select/BarSelectPlugin';

export const commonLineChartDatasetOptions: Partial<ChartDataset<'line', ScenarioTimeFrame[]>> = {
  stepped: false,
  pointRadius: 0,
  pointHitRadius: POINT_HOVER_RADIUS * 3,
  pointHoverRadius: POINT_HOVER_RADIUS,
  borderWidth: 3,
  borderCapStyle: 'round',
  borderJoinStyle: 'round',
};

export interface LineChartScenarioProps extends LineChartByProps {
  filled?: boolean;
  getDataSetLabel?: (id: string) => string;
  hideCondition?: (label: string) => boolean;
  enableCustomTimeRange?: boolean;
  setCustomTimeRange?: (customTimeRange: CustomTimeRangeParams) => void;
}

export const LineChartScenario = <FiltersSubset extends ScenarioQueryParams>({
  scenario,
  getDataSetLabel,
  filled,
  colorScheme,
  includeTimeRange = true,
  timeRange,
  tooltipTransKey,
  includeAssetId = false,
  assetId,
  filterGroup,
  hideCondition,
  enableCustomTimeRange = false,
  setCustomTimeRange,
}: LineChartScenarioProps) => {
  const { t } = useTranslation();
  const { getNumberText } = useLocaleFormatter();

  const { siteId, dateTimeFormat, siteTimezone } = useAppSelector(state => state.auth);
  const { darkTheme } = useAppSelector(state => state.prefs);

  const timeRangeFilters = useChartScenarioTimeFilters({
    scenario,
    includeTimeRange,
    timeRange,
  });

  const filters = useFilters<FiltersSubset>(filterGroup);

  const locationFilters = useLocationFilterParams({
    filterGroup,
    includeAssetId,
    assetId,
  });

  // Skip if assetId is expected but undefined (loading) for charts that need it.
  const assetIdIsMissing = includeAssetId && typeof assetId === 'undefined';

  const { data: chartData, isFetching } = useGetScenarioTimeSeriesQuery(
    {
      scenario,
      query: {
        ...filters,
        ...locationFilters,
        ...timeRangeFilters,
        assetIds: includeAssetId ? assetId : undefined,
      },
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: typeof siteId === 'undefined' || assetIdIsMissing,
    },
  );

  const data = useMemo<ChartData<'line', ScenarioTimeFrame[]>>(() => {
    let displayLabels: string[] = [];
    const dataSets: ChartDataset<'line', ScenarioTimeFrame[]>[] = [];

    if (chartData) {
      displayLabels = chartData.timestamps;

      const stackedColors = getColorScheme(colorScheme);

      for (let i = 0; i < chartData.dataSets.length; i++) {
        const item = chartData.dataSets[i];
        const themeColors = stackedColors[i % stackedColors.length];
        const color = darkTheme ? themeColors[1] : themeColors[0];
        const label = getDataSetLabel?.(item.label) ?? item.label;
        const hidden = hideCondition?.(item.label);

        dataSets.push({
          ...commonLineChartDatasetOptions,
          label,
          data: item.frames,
          backgroundColor: color + '19', // Approx 0.1 opacity in hex
          borderColor: color,
          fill: filled === true,
          hidden,
        });
      }
    }

    return {
      labels: displayLabels,
      datasets: dataSets,
    };
  }, [chartData, filled, colorScheme, darkTheme, getDataSetLabel, hideCondition]);

  const wrappedGetNumberText = useCallback<OnGetValue>(
    (value: number | string) => {
      return typeof value === 'number' ? getNumberText(value) : value;
    },
    [getNumberText],
  );

  const chartOptions = useMemo<ChartOptions<'line'>>(() => {
    // Default the timeRange to 24 hours if the query has not returned yet.
    const timeCategory = chartData?.timeCategory ?? TimeUnit.Hours;

    return getLineChartOptions(
      darkTheme,
      siteTimezone,
      dateTimeFormat,
      timeCategory,
      chartData?.timestamps ?? [],
      t,
      tooltipTransKey,
      enableCustomTimeRange,
      wrappedGetNumberText,
      setCustomTimeRange,
    );
  }, [
    darkTheme,
    t,
    tooltipTransKey,
    siteTimezone,
    dateTimeFormat,
    chartData?.timeCategory,
    chartData?.timestamps,
    enableCustomTimeRange,
    setCustomTimeRange,
    wrappedGetNumberText,
  ]);

  const numLabels = data?.labels?.length ?? 0;

  return (
    <ChartManager
      isLoading={typeof chartData === 'undefined'}
      isFetching={isFetching}
      hasData={numLabels > 1}
      height={getLineChartHeight()}
    >
      <Line options={chartOptions} data={data} plugins={[customRangePlugin]} />
    </ChartManager>
  );
};

export default LineChartScenario;
